/**
 * A identidade visual de um formulário: o catálogo, a cor livre e a regra que
 * decide se uma cor livre pode ir para a rua.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A decisão de produto que este arquivo carrega                           │
 * │                                                                          │
 * │ A §6 da especificação escolheu catálogo em vez de seletor de cor, e      │
 * │ escreveu o motivo: "um seletor livre entrega ao organizador o poder de   │
 * │ publicar um formulário ilegível — e quem paga é o convidado dele, que    │
 * │ não escolheu nada". A §12 deixou a porta aberta: aceitar hexadecimal é   │
 * │ evolução possível, e "exige derivar hover/lavagem e RECUSAR o que não    │
 * │ passa no contraste".                                                     │
 * │                                                                          │
 * │ É essa porta que este arquivo abre — com a fechadura junto. O catálogo   │
 * │ continua sendo o caminho normal e continua sendo o que volta quando a    │
 * │ cor livre é removida: `marca.tema` nunca deixa de valer.                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Nada aqui escreve cor. O que existe são coordenadas — número solto — e as
 * funções de `servicos/cor.ts`. O primeiro parágrafo daquele arquivo explica
 * por que isso não viola a regra do literal.
 */

import {
  arredondarContraste,
  contraste,
  distanciaDeMatiz,
  hexParaRgb,
  normalizarHexadecimal,
  oklchParaHex,
  oklchParaRgb,
  rgbParaOklch,
  type Oklch,
  type Rgb,
} from './cor';
import { FONTES, MODOS, type Fonte, type Marca, type Modo } from '../tipos';

/* ══ O catálogo ══════════════════════════════════════════════════════════ */

/**
 * Os 20 da Régua, com **matiz e croma** — não com a cor.
 *
 * `telas/Convite/Moldura.tsx` deliberadamente NÃO tem esta lista, e o
 * comentário lá explica: sanear o nome em vez de conferi-lo contra uma cópia é
 * o que impede um tema novo da Régua de nascer invisível para o convite. Aqui
 * a lista é inevitável — o editor precisa OFERECER as vinte escolhas, e não há
 * como oferecer o que não se enumera.
 *
 * O que faz dela uma cópia segura é `testes/marca.test.ts`, que lê
 * `src/estilo/temas.css` e reprova qualquer divergência: nome que sobrou, nome
 * que faltou, matiz que andou. Um tema novo na Régua quebra o build em vez de
 * sumir em silêncio, que era exatamente o risco.
 *
 * A COR de cada amostra não vem daqui: vem do próprio `temas.css`, aplicado
 * por `data-tema` no botão da amostra. Duplicar hexadecimal aqui seria a
 * infração que `disciplina.test.ts` existe para pegar — e, pior, criaria uma
 * segunda verdade sobre o que é "púrpura".
 *
 * Matiz e croma existem só para **achar o tema mais próximo** de uma cor livre
 * recusada. São geometria, não aparência.
 */
export const TEMAS = [
  { nome: 'azul', matiz: 264.2, croma: 0.246 },
  { nome: 'indigo', matiz: 275.0, croma: 0.211 },
  { nome: 'violeta', matiz: 294.9, croma: 0.21 },
  { nome: 'purpura', matiz: 309.9, croma: 0.21 },
  { nome: 'fucsia', matiz: 330.2, croma: 0.21 },
  { nome: 'rosa', matiz: 345.2, croma: 0.21 },
  { nome: 'laranja', matiz: 60.2, croma: 0.134 },
  { nome: 'cobre', matiz: 49.8, croma: 0.153 },
  { nome: 'bronze', matiz: 74.8, croma: 0.12 },
  { nome: 'oliva', matiz: 109.8, croma: 0.122 },
  { nome: 'musgo', matiz: 125.1, croma: 0.14 },
  { nome: 'verde', matiz: 145.0, croma: 0.172 },
  { nome: 'esmeralda', matiz: 161.6, croma: 0.121 },
  { nome: 'verde-mar', matiz: 178.6, croma: 0.099 },
  { nome: 'petroleo', matiz: 195.7, croma: 0.094 },
  { nome: 'ciano', matiz: 215.2, croma: 0.098 },
  { nome: 'ceu', matiz: 235.5, croma: 0.119 },
  { nome: 'cobalto', matiz: 249.8, croma: 0.159 },
  { nome: 'ardosia', matiz: 258.6, croma: 0.071 },
  { nome: 'grafite', matiz: 261.1, croma: 0.029 },
] as const satisfies readonly { nome: string; matiz: number; croma: number }[];

export type NomeDeTema = (typeof TEMAS)[number]['nome'];

/** O acento do painel, e o padrão de formulário novo. Uma aplicação, um acento. */
export const TEMA_PADRAO: NomeDeTema = 'purpura';

export const MARCA_PADRAO: Marca = {
  tema: TEMA_PADRAO,
  modo: 'auto',
  fonte: 'sistema',
  logo_url: null,
  capa_url: null,
  acento: null,
  video_url: null,
};

/* ══ Os fundos contra os quais medimos ═══════════════════════════════════ */

/**
 * `--fundo` de cada modo, copiado de `tokens.css` **em coordenada**.
 *
 * É a única duplicação inevitável do arquivo: a verificação de contraste
 * precisa do fundo real do convite, e o CSS não devolve valor para o
 * JavaScript sem um navegador montado — que é justamente o que a suíte não
 * tem. `testes/marca.test.ts` compara estes números com `tokens.css` e reprova
 * se um dos dois andar sem o outro.
 *
 * Medir contra o fundo TINTADO, e não contra branco puro, é o certo: é ele que
 * o convidado vê, e a tintura custa cerca de um décimo de razão de contraste —
 * exatamente na faixa em que a decisão vira sim ou não.
 */
const FUNDO: Readonly<Record<'claro' | 'escuro', Oklch>> = {
  claro: { L: 0.991, C: 0.003, h: 310 },
  escuro: { L: 0.17, C: 0.008, h: 310 },
};

/**
 * Os dois `--sobre-acento` da Régua, em coordenada.
 *
 * A Régua usa o branco no modo claro e um quase-marinho no escuro. Aqui os
 * dois são CANDIDATOS nos dois modos, e quem escolhe é o contraste: uma marca
 * de cor clara precisa de rótulo escuro no botão, e travar o branco por ser o
 * do modo claro entregaria um botão com o texto sumindo.
 */
const SOBRE_CANDIDATOS: readonly Oklch[] = [
  { L: 1, C: 0, h: 0 },
  { L: 0.1831, C: 0.0309, h: 263.4 },
];

/* ══ A regra de contraste ════════════════════════════════════════════════ */

/**
 * Os três pares que o acento forma no convite, e o piso de cada um.
 *
 * Vieram de ler `src/estilo/convite.css`, não de suposição — o acento aparece
 * ali em exatamente três papéis:
 *
 * | par | onde | piso | norma |
 * | --- | --- | --- | --- |
 * | `--sobre-acento` sobre `--acento` | o rótulo do botão primário | **4,5:1** | WCAG 1.4.3, texto normal |
 * | `--acento` sobre `--fundo` | o anel de foco e a borda da opção escolhida | **3:1** | WCAG 1.4.11, não-texto |
 * | `--acento` sobre `--acentoc` | o rótulo da opção escolhida, sobre a lavagem | **3:1** | WCAG 1.4.11 |
 *
 * **Por que o terceiro não é 4,5, sendo texto.** Medidos os 20 do catálogo,
 * esse par vai de 3,91 a 5,05 — ou seja, a própria Régua entrega abaixo de
 * 4,5 na maioria dos temas. Um piso de 4,5 ali reprovaria a referência, e uma
 * regra que chama de ilegível o catálogo que ela deveria proteger está errada
 * sobre alguma coisa. O que segura o caso é a outra regra do `DESIGN.md`:
 * estado nunca é comunicado só por cor — a opção escolhida tem borda, lavagem
 * E rótulo, e nenhum deles sozinho carrega a informação.
 *
 * O primeiro par, o do botão, é 4,5 sem desconto: ali a cor é o fundo do
 * texto que a pessoa precisa ler para clicar, e não há segundo portador.
 */
export const PISOS = {
  botao: 4.5,
  traco: 3,
  lavagem: 3,
} as const;

/** Como o acento é derivado. Os números saíram de medir os 20 do catálogo. */
const RECEITA = {
  /**
   * Quanto o hover se afasta do acento, em lightness.
   *
   * A DIREÇÃO não está aqui de propósito: ela cai do princípio, em
   * `derivarAcento`. O hover se afasta do rótulo do botão — o que no modo
   * claro dá escurecer e no escuro dá clarear, que é precisamente o que a
   * Régua faz (−5,2 e +5,5 pontos, medidos). Fixar a direção por modo
   * funcionaria para os 20 e quebraria numa marca clara demais.
   */
  passoDoHover: 0.053,
  /**
   * A lavagem: quase o fundo, com um sopro do matiz do acento.
   *
   * `preferida` é a da Régua, medida nos 20 (95,4% no claro, 26% no escuro), e
   * é onde a lavagem fica quando pode. `minima`/`maxima` são a faixa em que ela
   * ainda LÊ COMO LAVAGEM: acima do teto claro ela se confunde com `--fundo`
   * (99,1%), abaixo do piso escuro ela fica mais escura que a página.
   *
   * ── Por que a faixa existe ──────────────────────────────────────────────
   *
   * A lavagem é NOSSA, o acento é de quem trouxe a marca. Reprovar a cor de uma
   * empresa por causa de um valor que nós escolhemos seria cobrar dela um erro
   * nosso. Medido: um azul de marca comum reprovava por 2,7:1 contra a lavagem
   * escura de 26% e alcança 3,0:1 com ela em 22% — dentro da faixa, e sem que a
   * lavagem deixe de ser lavagem.
   *
   * A direção do ajuste sai do mesmo princípio do hover: a lavagem se AFASTA do
   * acento que vai ficar escrito em cima dela.
   *
   * O croma é o do acento com teto: croma alto num quase-branco vira mancha.
   */
  lavagem: {
    claro: { preferida: 0.954, minima: 0.93, maxima: 0.985, tetoDeCroma: 0.03 },
    escuro: { preferida: 0.26, minima: 0.19, maxima: 0.3, tetoDeCroma: 0.055 },
  },
  /** De quanto em quanto a lavagem anda ao procurar o piso. */
  passoDaLavagem: 0.005,
} as const;

export type ModoConcreto = 'claro' | 'escuro';

/**
 * Os modos que de fato podem acontecer com o convidado.
 *
 * `auto` é os dois — e é a razão de a verificação não poder olhar um só. O
 * organizador que escolhe `auto` não está escolhendo o modo dele: está
 * delegando ao aparelho de quem recebe o convite, e um acento aprovado só no
 * claro chega quebrado para metade das pessoas.
 *
 * Quando o modo é fixo, verificar o outro seria recusar por um cenário que
 * não existe — e o organizador ficaria sem entender por que a cor dele foi
 * negada por causa de um modo escuro que ninguém vai ver.
 */
export function modosPossiveis(modo: Modo): readonly ModoConcreto[] {
  if (modo === 'claro') return ['claro'];
  if (modo === 'escuro') return ['escuro'];
  return ['claro', 'escuro'];
}

/** As seis variáveis da Régua, para um modo, derivadas de um acento. */
export type AcentoDerivado = {
  readonly acento: string;
  readonly hover: string;
  readonly lavagem: string;
  readonly sobre: string;
};

/**
 * A lavagem: fica na lightness da Régua se puder, e se afasta do acento até
 * alcançar o piso se precisar — sem sair da faixa em que ainda é lavagem.
 *
 * O passo é pequeno e a busca para na PRIMEIRA lightness que serve. É de
 * propósito: ir direto ao extremo da faixa daria mais contraste e uma lavagem
 * que não parece com as outras dezenove, e a semelhança entre elas é o que faz
 * o produto parecer de uma peça só.
 */
function derivarLavagem(base: Oklch, acento: Rgb, modo: ModoConcreto): Oklch {
  const receita = RECEITA.lavagem[modo];
  const croma = Math.min(base.C, receita.tetoDeCroma);
  const comLightness = (L: number): Oklch => ({ L, C: croma, h: base.h });

  const preferida = comLightness(receita.preferida);
  if (contraste(oklchParaRgb(preferida), acento) >= PISOS.lavagem) return preferida;

  // Afasta-se do acento: lavagem clara clareia, lavagem escura escurece. É o
  // mesmo princípio do hover, e por isso não há tabela de direção por modo.
  const direcao = receita.preferida > base.L ? 1 : -1;
  const limite = direcao > 0 ? receita.maxima : receita.minima;

  let melhor = preferida;
  for (
    let L = receita.preferida;
    direcao > 0 ? L <= limite : L >= limite;
    L += direcao * RECEITA.passoDaLavagem
  ) {
    const tentativa = comLightness(L);
    melhor = tentativa;
    if (contraste(oklchParaRgb(tentativa), acento) >= PISOS.lavagem) return tentativa;
  }

  // Nem no extremo da faixa deu. Devolve o extremo assim mesmo: quem reprova é
  // `avaliarAcento`, e reprovar com a MELHOR lavagem possível é o que faz o
  // número na mensagem ser o número de verdade.
  return melhor;
}

function melhorSobre(acento: Rgb): { cor: Oklch; razao: number } {
  let melhor = { cor: SOBRE_CANDIDATOS[0] as Oklch, razao: 0 };
  for (const candidato of SOBRE_CANDIDATOS) {
    const razao = contraste(oklchParaRgb(candidato), acento);
    if (razao > melhor.razao) melhor = { cor: candidato, razao };
  }
  return melhor;
}

/**
 * Hover e lavagem a partir do acento. Puro, e sem opinião sobre aprovar.
 *
 * `derivarAcento` deriva mesmo quando o acento reprova — quem julga é
 * `avaliarAcento`. Separar as duas é o que permite a tela mostrar a amostra do
 * que a pessoa digitou ao lado do motivo da recusa: ver a cor que não passou é
 * metade da explicação.
 */
export function derivarAcento(hex: string, modo: ModoConcreto): AcentoDerivado | null {
  const rgb = hexParaRgb(hex);
  if (!rgb) return null;

  const base = rgbParaOklch(rgb);
  const sobre = melhorSobre(rgb);

  // O hover se afasta do rótulo do botão: se o rótulo é claro, o hover
  // escurece; se é escuro, clareia. Aproximar-se dele apagaria o texto do
  // botão no exato momento em que a pessoa está prestes a clicar.
  const direcao = sobre.cor.L > base.L ? -1 : 1;
  const hover: Oklch = {
    ...base,
    L: Math.min(1, Math.max(0, base.L + direcao * RECEITA.passoDoHover)),
  };

  const lavagem = derivarLavagem(base, rgb, modo);

  return {
    acento: normalizarHexadecimal(hex) ?? hex,
    hover: oklchParaHex(hover),
    lavagem: oklchParaHex(lavagem),
    sobre: oklchParaHex(sobre.cor),
  };
}

/** Um par medido, com o nome que a tela mostra e o piso que ele tinha de vencer. */
export type Medida = {
  readonly par: 'botao' | 'traco' | 'lavagem';
  readonly rotulo: string;
  readonly onde: string;
  readonly razao: number;
  readonly piso: number;
  readonly passou: boolean;
};

export type AvaliacaoDeModo = {
  readonly modo: ModoConcreto;
  readonly derivado: AcentoDerivado;
  readonly medidas: readonly Medida[];
  readonly passou: boolean;
};

export type AvaliacaoDoAcento =
  | { readonly ok: false; readonly motivo: 'forma'; readonly mensagem: string }
  | {
      readonly ok: true;
      readonly hex: string;
      readonly aprovado: boolean;
      readonly porModo: readonly AvaliacaoDeModo[];
      /** Preenchido só quando reprova: o do catálogo mais perto do que se pediu. */
      readonly sugestao: NomeDeTema | null;
      /** Uma frase pronta, escrita para o organizador. Vazia quando aprova. */
      readonly mensagem: string;
    };

const ROTULOS: Readonly<Record<Medida['par'], { rotulo: string; onde: string }>> = {
  botao: {
    rotulo: 'rótulo sobre o botão',
    onde: 'o texto do botão de avançar, que o convidado precisa ler para clicar',
  },
  traco: {
    rotulo: 'acento sobre o fundo',
    onde: 'o anel de foco e a borda da opção escolhida',
  },
  lavagem: {
    rotulo: 'acento sobre a lavagem',
    onde: 'o rótulo da opção escolhida, dentro do realce',
  },
};

function medir(par: Medida['par'], razaoCrua: number): Medida {
  const razao = arredondarContraste(razaoCrua);
  const piso = PISOS[par];
  const texto = ROTULOS[par];
  return { par, rotulo: texto.rotulo, onde: texto.onde, razao, piso, passou: razao >= piso };
}

function avaliarModo(hex: string, modo: ModoConcreto): AvaliacaoDeModo | null {
  const derivado = derivarAcento(hex, modo);
  if (!derivado) return null;

  const acento = hexParaRgb(derivado.acento);
  const sobre = hexParaRgb(derivado.sobre);
  const lavagem = hexParaRgb(derivado.lavagem);
  if (!acento || !sobre || !lavagem) return null;

  const fundo = oklchParaRgb(FUNDO[modo]);

  const medidas: readonly Medida[] = [
    medir('botao', contraste(sobre, acento)),
    medir('traco', contraste(acento, fundo)),
    medir('lavagem', contraste(acento, lavagem)),
  ];

  return { modo, derivado, medidas, passou: medidas.every((m) => m.passou) };
}

/**
 * O tema do catálogo mais parecido com uma cor.
 *
 * Distância em matiz primeiro, croma como desempate e com peso pequeno: quem
 * pediu um verde e ouviu não quer outro verde, mesmo que menos saturado.
 * Devolver um roxo porque a saturação bate seria responder outra pergunta.
 *
 * Cinza (croma quase zero) não tem matiz — `rgbParaOklch` devolve 0 e a busca
 * por matiz cairia num vermelho qualquer. O atalho para `grafite` é o único
 * caso especial, e é o tema que a Régua reserva justamente para isso.
 */
export function temaMaisProximo(hex: string): NomeDeTema | null {
  const rgb = hexParaRgb(hex);
  if (!rgb) return null;

  const { C, h } = rgbParaOklch(rgb);
  if (C < 0.02) return 'grafite';

  let melhor: NomeDeTema | null = null;
  let menorDistancia = Number.POSITIVE_INFINITY;

  for (const tema of TEMAS) {
    const distancia = distanciaDeMatiz(h, tema.matiz) + Math.abs(C - tema.croma) * 20;
    if (distancia < menorDistancia) {
      menorDistancia = distancia;
      melhor = tema.nome;
    }
  }

  return melhor;
}

const NOME_DO_MODO: Readonly<Record<ModoConcreto, string>> = {
  claro: 'claro',
  escuro: 'escuro',
};

/**
 * O veredito sobre uma cor livre — a peça que a §12 pediu.
 *
 * A mensagem de recusa nomeia **o par, o número e o modo**, porque "contraste
 * insuficiente" não diz o que fazer. "No modo escuro, o acento sobre o fundo
 * dá 1,8:1 e precisa de 3" diz: escureça menos, ou fixe o modo claro. As duas
 * saídas existem, e a segunda é a que o organizador não descobriria sozinho.
 */
export function avaliarAcento(hexBruto: string, modo: Modo): AvaliacaoDoAcento {
  const hex = normalizarHexadecimal(hexBruto);
  if (hex === null) {
    return {
      ok: false,
      motivo: 'forma',
      mensagem:
        'Isto não é uma cor. Escreva o hexadecimal da sua marca com 3 ou 6 dígitos — ' +
        'a cerquilha é opcional.',
    };
  }

  const porModo: AvaliacaoDeModo[] = [];
  for (const concreto of modosPossiveis(modo)) {
    const avaliacao = avaliarModo(hex, concreto);
    if (avaliacao) porModo.push(avaliacao);
  }

  const aprovado = porModo.length > 0 && porModo.every((m) => m.passou);
  if (aprovado) {
    return { ok: true, hex, aprovado: true, porModo, sugestao: null, mensagem: '' };
  }

  const falhas = porModo.flatMap((m) =>
    m.medidas.filter((medida) => !medida.passou).map((medida) => ({ modo: m.modo, medida })),
  );

  const sugestao = temaMaisProximo(hex);
  const partes = falhas.map(
    ({ modo: concreto, medida }) =>
      `no modo ${NOME_DO_MODO[concreto]}, ${medida.rotulo} dá ${formatarRazao(medida.razao)} e ` +
      `precisa de ${formatarRazao(medida.piso)} — é ${medida.onde}`,
  );

  const ondeFalhou = partes.join('; ');
  const alternativa =
    modo === 'auto' && porModo.some((m) => m.passou)
      ? ` Ela passa no modo ${NOME_DO_MODO[porModo.find((m) => m.passou)?.modo ?? 'claro']}: ` +
        'fixar o modo, em vez de deixar no automático, também resolve.'
      : '';

  return {
    ok: true,
    hex,
    aprovado: false,
    porModo,
    sugestao,
    mensagem:
      `Esta cor deixa o convite difícil de ler: ${ondeFalhou}.` +
      (sugestao ? ` O tema mais próximo dela no catálogo é ${sugestao}.` : '') +
      alternativa,
  };
}

/** `4.5` vira `4,5` — vírgula decimal, como todo número do produto. */
export function formatarRazao(razao: number): string {
  return `${razao.toFixed(1).replace('.', ',')}:1`;
}

/* ══ Da marca para o CSS ═════════════════════════════════════════════════ */

/**
 * O nome do atributo que liga o bloco da cor livre, em `temas.css`.
 *
 * Ele convive com `data-tema` em vez de substituí-lo, e isso é deliberado em
 * dois pontos. Primeiro: os NEUTROS do convite vêm de `:root, [data-tema]` em
 * `tokens.css` — sem `data-tema` no contêiner, um convite escuro perderia o
 * fundo escuro e ficaria com o do navegador. Segundo: tirar a cor livre tem de
 * devolver o formulário ao tema que ele já tinha, sem uma segunda gravação.
 */
export const ATRIBUTO_DO_ACENTO = 'data-acento';
export const ACENTO_LIVRE = 'livre';

/**
 * As oito variáveis que a cor livre injeta no contêiner, quatro por modo.
 *
 * Duas por modo não bastariam e um par só não serviria: `modo: auto` significa
 * que os dois vão acontecer, no mesmo elemento, decididos pelo aparelho do
 * convidado — e estilo em linha não sabe responder a `prefers-color-scheme`.
 * Quem escolhe entre os dois conjuntos é o CSS, no bloco `[data-acento]` de
 * `temas.css`, que repete os três estados da Régua.
 */
export type VariaveisDoAcento = Readonly<Record<string, string>>;

export function variaveisDoAcento(hex: string): VariaveisDoAcento | null {
  const claro = derivarAcento(hex, 'claro');
  const escuro = derivarAcento(hex, 'escuro');
  if (!claro || !escuro) return null;

  return {
    '--acento-livre-claro': claro.acento,
    '--acento-hover-livre-claro': claro.hover,
    '--acentoc-livre-claro': claro.lavagem,
    '--sobre-acento-livre-claro': claro.sobre,
    '--acento-livre-escuro': escuro.acento,
    '--acento-hover-livre-escuro': escuro.hover,
    '--acentoc-livre-escuro': escuro.lavagem,
    '--sobre-acento-livre-escuro': escuro.sobre,
  };
}

/* ══ A imagem ════════════════════════════════════════════════════════════ */

/**
 * Os tipos aceitos, e a MESMA lista que a migração 0009 grava no bucket.
 *
 * Repetir não é redundância, e a ordem das duas verificações é o ponto: aqui a
 * recusa vira uma frase que diz o que fazer, e lá vira um `400` do serviço de
 * Storage com um texto de biblioteca em inglês. A daqui é cortesia; a de lá é o
 * controle, porque a tela é trocável e o banco não.
 *
 * SVG está na lista com uma ressalva que vale escrever: SVG é documento, não
 * imagem — ele pode carregar script. O que o torna aceitável aqui é o Storage
 * servir do próprio domínio dele, e o `<img>` do convite não executar script de
 * SVG carregado por `src`. Se um dia alguém embutir o logo com `<object>` ou
 * `<use>`, esta linha precisa sair junto.
 */
export const TIPOS_DE_IMAGEM = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'] as const;

/** 2 MiB — o mesmo teto do bucket. */
export const TAMANHO_MAXIMO = 2 * 1024 * 1024;

const EXTENSAO: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
};

export type ExameDaImagem =
  | { readonly ok: true; readonly extensao: string }
  | { readonly ok: false; readonly mensagem: string };

/** Quantos MB, com uma casa e vírgula. `1536000` vira `1,5 MB`. */
function emMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * A verificação que acontece ANTES de o byte sair do navegador.
 *
 * Subir para depois receber a recusa é o pior dos dois mundos: o organizador
 * espera o envio de um arquivo de 8 MB por uma conexão de celular para então
 * ler que ele não cabia. E a mensagem que volta do Storage é de biblioteca —
 * ela nomeia o limite em bytes e não diz o que fazer.
 */
/* ══ O vídeo de abertura ═════════════════════════════════════════════════
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ Quem manda é o banco.                                                    │
 * │                                                                          │
 * │ A CHECK `formulario_video_valido` (migração 0012) já recusa qualquer     │
 * │ endereço fora desta lista — nenhum `video_url` inválido existe na tabela.│
 * │ Este arquivo repete a regra do lado de cá pelo MESMO motivo de           │
 * │ `util/acao.ts` repetir a de `no_acao_valida`: o organizador precisa      │
 * │ ouvir "não" enquanto digita, e não só quando a gravação falhar com uma   │
 * │ mensagem de constraint; e `src` de `<iframe>` não é texto — um `src`     │
 * │ mal filtrado carrega o mesmo risco de um `href` mal filtrado.            │
 * │                                                                          │
 * │ Quando um lado mudar, o outro muda junto — `testes/marca.test.ts` compara│
 * │ os dois nos mesmos casos.                                                │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

/**
 * Lista BRANCA de players — não de vídeos. `youtube.com/watch?v=…` (a página)
 * fica de fora de propósito: só o endereço de EMBED entra num `<iframe>` sem
 * X-Frame-Options recusando a montagem, e aceitar a página levaria o
 * organizador a colar o link errado e descobrir só quando o convite abrisse
 * em branco.
 */
export const PADROES_DE_VIDEO: readonly { readonly nome: string; readonly padrao: RegExp }[] = [
  { nome: 'Bunny Stream', padrao: /^https:\/\/iframe\.mediadelivery\.net\/play\/[^\s<>"]+$/ },
  { nome: 'YouTube', padrao: /^https:\/\/www\.youtube\.com\/embed\/[^\s<>"]+$/ },
  { nome: 'Vimeo', padrao: /^https:\/\/player\.vimeo\.com\/video\/[^\s<>"]+$/ },
];

/** O mesmo teto do banco: `length(marca ->> 'video_url') <= 2000`. */
export const MAXIMO_DO_VIDEO = 2000;

/**
 * O endereço se ele puder virar `src` de `<iframe>`, `null` se não puder.
 *
 * Devolver `null` em vez de lançar é o mesmo acordo de `enderecoPermitido` em
 * `util/acao.ts`: quem chama é uma tela (o editor) ou um componente que
 * decide se monta o player (o convite), e nenhum dos dois deve quebrar por
 * causa de um endereço mal digitado.
 */
export function enderecoDeVideoPermitido(bruto: unknown): string | null {
  if (typeof bruto !== 'string') return null;
  if (bruto.length === 0 || bruto.length > MAXIMO_DO_VIDEO) return null;
  // Sem `trim`: comparado como está gravado, que é como o banco compara.
  return PADROES_DE_VIDEO.some(({ padrao }) => padrao.test(bruto)) ? bruto : null;
}

/** O motivo, escrito para quem está digitando. `null` quando não há motivo. */
export function motivoDoVideoRecusado(bruto: string): string | null {
  if (bruto.trim() === '') return null;
  if (enderecoDeVideoPermitido(bruto) !== null) return null;
  if (bruto.length > MAXIMO_DO_VIDEO) return 'Endereço longo demais.';
  if (/^http:\/\//i.test(bruto)) {
    return 'Endereço em http:// não entra: o convite viaja por rede que ninguém controla. Use https://.';
  }
  if (/\s/.test(bruto)) return 'O endereço não pode ter espaço.';
  const nomes = PADROES_DE_VIDEO.map((p) => p.nome).join(', ');
  return (
    `Isto não é um link de player. O convite só aceita o endereço de EMBED de ${nomes} — ` +
    'na maioria dos players é a opção "Compartilhar → Incorporar", não o link da página do vídeo.'
  );
}

/**
 * O veredito sobre um endereço de vídeo — o par de `AvaliacaoDoAcento`, na
 * mesma forma que `useMarca.ts` já sabe consumir: `ok: false` é malformado
 * (aqui não existe essa fase — todo texto não vazio ou passa ou é recusado, e
 * quem decide "digitando" ou "vazio" é o CHAMADOR, como em `aoDigitarAcento`);
 * `ok: true` é o endereço pronto para gravar.
 *
 * Deliberadamente mais simples que `AvaliacaoDoAcento`: não há "passa num modo
 * e não no outro" para um link de player — ou o host está na lista, ou não
 * está, em qualquer modo.
 */
export type AvaliacaoDoVideo =
  { readonly ok: true; readonly url: string } | { readonly ok: false; readonly mensagem: string };

/** `motivoDoVideoRecusado` devolve `null` para vazio; aqui vazio é sempre recusa. */
export function avaliarVideo(bruto: string): AvaliacaoDoVideo {
  const aceito = enderecoDeVideoPermitido(bruto);
  if (aceito !== null) return { ok: true, url: aceito };
  return { ok: false, mensagem: motivoDoVideoRecusado(bruto) ?? 'Escreva o endereço do vídeo.' };
}

export function examinarImagem(arquivo: File): ExameDaImagem {
  const extensao = EXTENSAO[arquivo.type];
  if (extensao === undefined) {
    return {
      ok: false,
      mensagem:
        `Este arquivo é ${arquivo.type === '' ? 'de tipo desconhecido' : arquivo.type}. ` +
        'O convite aceita PNG, JPEG, WebP e SVG.',
    };
  }

  if (arquivo.size > TAMANHO_MAXIMO) {
    return {
      ok: false,
      mensagem:
        `Esta imagem tem ${emMegabytes(arquivo.size)} e o limite é ` +
        `${emMegabytes(TAMANHO_MAXIMO)}. Logo de convite não precisa de mais — e o ` +
        'convidado que abrir no celular paga o download.',
    };
  }

  if (arquivo.size === 0) {
    return { ok: false, mensagem: 'Este arquivo está vazio.' };
  }

  return { ok: true, extensao };
}

/* ══ Normalização ════════════════════════════════════════════════════════ */

function textoOuNulo(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() !== '' ? valor.trim() : null;
}

/**
 * O `jsonb` do banco vira `Marca` — e um `jsonb` pode ser qualquer coisa.
 *
 * A coluna tem default, mas default só vale para linha nova: uma linha gravada
 * antes de a chave `acento` existir volta sem ela, e é por isso que a
 * normalização acontece na leitura e não confia na forma.
 *
 * Não é a mesma verificação de `Moldura.tsx`, e nem deveria ser: lá o alvo é
 * saneamento de atributo (o que pode virar `data-tema` sem quebrar o CSS),
 * aqui é o estado que o EDITOR manipula, que precisa de valores dentro do
 * catálogo para o controle ter o que marcar como escolhido.
 */
export function normalizarMarca(bruta: unknown): Marca {
  if (typeof bruta !== 'object' || bruta === null) return MARCA_PADRAO;
  const objeto = bruta as Record<string, unknown>;

  const tema = textoOuNulo(objeto['tema'])?.toLowerCase() ?? '';
  const modo = textoOuNulo(objeto['modo']) ?? '';
  const fonte = textoOuNulo(objeto['fonte']) ?? '';
  const acento = textoOuNulo(objeto['acento']);

  return {
    tema: (TEMAS as readonly { nome: string }[]).some((t) => t.nome === tema)
      ? tema
      : MARCA_PADRAO.tema,
    modo: (MODOS as readonly string[]).includes(modo) ? (modo as Modo) : MARCA_PADRAO.modo,
    fonte: (FONTES as readonly string[]).includes(fonte) ? (fonte as Fonte) : MARCA_PADRAO.fonte,
    logo_url: textoOuNulo(objeto['logo_url']),
    capa_url: textoOuNulo(objeto['capa_url']),
    // Cor que não é cor vira ausência, e o formulário cai no tema do catálogo.
    // Guardar lixo aqui faria o convite perder o acento sem dizer por quê.
    acento: acento === null ? null : normalizarHexadecimal(acento),
    // Sem `enderecoDeVideoPermitido` aqui de propósito: esta função lê o que
    // está gravado (a CHECK já filtrou), não o que está sendo digitado — é a
    // MESMA leniência de `logo_url`/`capa_url`, que também não revalidam
    // esquema na leitura. Quem recusa é o editor, antes de gravar.
    video_url: textoOuNulo(objeto['video_url']),
  };
}

/**
 * A cor livre em uso, ou `null`.
 *
 * Uma cor gravada que **deixou de passar** no contraste continua sendo
 * devolvida: recusar na leitura apagaria a identidade de um formulário já
 * publicado por causa de uma regra que mudou depois. A recusa mora no editor,
 * onde há alguém para ler o motivo e decidir.
 */
export function acentoEmUso(marca: Marca): string | null {
  return marca.acento === null || marca.acento === undefined
    ? null
    : normalizarHexadecimal(marca.acento);
}
